import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Camera,
  Video,
  Home,
  Building2,
  Warehouse,
  TreePine,
  Coffee,
  UtensilsCrossed,
  Dumbbell,
  Users,
  Briefcase,
  Car,
  Ship,
} from 'lucide-react';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { constructQueryParamName } from '../../util/search';
import { parse } from '../../util/urlHelpers';

import css from './CategoryIconNav.module.css';

const categoryIconNavItems = [
  {
    id: 'all_spaces',
    label: 'All Spaces',
    icon: LayoutGrid,
    categoryLevel1: null,
    categoryLevel2: null,
  },
  {
    id: 'photo_studio',
    label: 'Photo Studio',
    icon: Camera,
    categoryLevel1: 'studio',
    categoryLevel2: 'studio_photography',
  },
  {
    id: 'film_studio',
    label: 'Film Studio',
    icon: Video,
    categoryLevel1: 'studio',
    categoryLevel2: 'studio_videography',
  },
  {
    id: 'houses',
    label: 'Houses',
    icon: Home,
    categoryLevel1: 'residential',
    categoryLevel2: null,
  },
  {
    id: 'rooftops',
    label: 'Rooftops',
    icon: Building2,
    categoryLevel1: 'urban',
    categoryLevel2: 'urban_rooftop',
  },
  {
    id: 'warehouses',
    label: 'Warehouses',
    icon: Warehouse,
    categoryLevel1: 'urban',
    categoryLevel2: 'urban_industrial_warehouse',
  },
  {
    id: 'outdoor',
    label: 'Outdoor',
    icon: TreePine,
    categoryLevel1: 'outdoor',
    categoryLevel2: null,
  },
  {
    id: 'cafes',
    label: 'Cafes',
    icon: Coffee,
    categoryLevel1: 'urban',
    categoryLevel2: 'urban_coffee_shop',
  },
  {
    id: 'restaurants',
    label: 'Restaurants',
    icon: UtensilsCrossed,
    categoryLevel1: 'urban',
    categoryLevel2: 'urban_restaurant_bar',
  },
  {
    id: 'sports',
    label: 'Sports',
    icon: Dumbbell,
    categoryLevel1: 'sports',
    categoryLevel2: null,
  },
  {
    id: 'public_spaces',
    label: 'Public Spaces',
    icon: Users,
    categoryLevel1: 'public_spaces',
    categoryLevel2: null,
  },
  {
    id: 'office_spaces',
    label: 'Office Spaces',
    icon: Briefcase,
    categoryLevel1: 'urban',
    categoryLevel2: 'urban_office_space',
  },
  {
    id: 'garages',
    label: 'Garages',
    icon: Car,
    categoryLevel1: 'transport',
    categoryLevel2: 'garages',
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    icon: Ship,
    categoryLevel1: 'transport',
    categoryLevel2: null,
  },
];

const findCategory = (categories, id) => (categories || []).find(category => category.id === id);

const hasConfiguredCategoryPath = (categories, item) => {
  if (!item.categoryLevel1) {
    return true;
  }

  const rootCategory = findCategory(categories, item.categoryLevel1);
  if (!rootCategory) {
    return false;
  }

  if (!item.categoryLevel2) {
    return true;
  }

  return !!findCategory(rootCategory.subcategories || [], item.categoryLevel2);
};

const getCategoryParamNames = categoryConfiguration => {
  const { key = 'categoryLevel', scope = 'public', categoryLevelKeys = [] } =
    categoryConfiguration || {};
  const level1Key = categoryLevelKeys[0] || `${key}1`;
  const level2Key = categoryLevelKeys[1] || `${key}2`;
  const allLevelKeys =
    categoryLevelKeys.length > 0 ? categoryLevelKeys : [level1Key, level2Key];
  const all = allLevelKeys.map(levelKey => constructQueryParamName(levelKey, scope));

  return {
    level1: constructQueryParamName(level1Key, scope),
    level2: constructQueryParamName(level2Key, scope),
    all,
  };
};

const omitCategoryParams = (params, categoryParamNames) =>
  Object.entries(params || {}).reduce((picked, [key, value]) => {
    const shouldOmit =
      categoryParamNames.all.includes(key) || key === 'page' || key === 'mapSearch';

    return shouldOmit ? picked : { ...picked, [key]: value };
  }, {});

const isActiveItem = (item, params, categoryParamNames) => {
  const activeLevel1 = params[categoryParamNames.level1];
  const activeLevel2 = params[categoryParamNames.level2];

  if (!item.categoryLevel1) {
    return !activeLevel1 && !activeLevel2;
  }

  if (item.categoryLevel2) {
    return activeLevel1 === item.categoryLevel1 && activeLevel2 === item.categoryLevel2;
  }

  return activeLevel1 === item.categoryLevel1 && !activeLevel2;
};

const CategoryIconNav = props => {
  const { className, variant = 'default' } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  const categoryConfiguration = config?.categoryConfiguration || {};
  const categories = categoryConfiguration.categories || [];
  const categoryParamNames = getCategoryParamNames(categoryConfiguration);
  const currentParams = parse(location.search || '');
  const items = categoryIconNavItems.filter(item => hasConfiguredCategoryPath(categories, item));

  if (items.length <= 1) {
    return null;
  }

  const handleClick = item => {
    const searchParams = omitCategoryParams(currentParams, categoryParamNames);

    if (item.categoryLevel1) {
      searchParams[categoryParamNames.level1] = item.categoryLevel1;
    }

    if (item.categoryLevel2) {
      searchParams[categoryParamNames.level2] = item.categoryLevel2;
    }

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  return (
    <nav
      className={classNames(css.root, className, {
        [css.hero]: variant === 'hero',
        [css.searchPage]: variant === 'searchPage',
      })}
      aria-label="Browse by category"
    >
      <div className={css.scrollViewport}>
        <div className={css.scroller}>
          {items.map(item => {
            const Icon = item.icon;
            const isActive = isActiveItem(item, currentParams, categoryParamNames);

            return (
              <button
                key={item.id}
                type="button"
                className={classNames(css.item, { [css.activeItem]: isActive })}
                onClick={() => handleClick(item)}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={css.icon} aria-hidden="true" />
                <span className={css.label}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

CategoryIconNav.propTypes = {
  className: string,
  variant: string,
};

export default CategoryIconNav;
